export const whiteLabelContent = {
  en: {
    // Navigation
    nav: {
      features: "Features",
      solutions: "Solutions",
      security: "Security",
      login: "Log In"
    },
    
    // Hero Section
    hero: {
      title: "Enterprise AI Agent Platform",
      subtitle: "Deploy powerful AI agents with complete control and data sovereignty",
      cta: "Get Started",
      login: "Log In"
    },
    
    // Features Section
    features: {
      title: "Platform Features",
      subtitle: "An enterprise-grade AI platform built for security and performance",
      items: [
        {
          icon: "brainCircuit",
          title: "Specialized AI Agents",
          description: "Ready-to-deploy AI agents for specific business functions like legal, HR, support, and customer service"
        },
        {
          icon: "shield",
          title: "Data Sovereignty",
          description: "Keep all your data within your security perimeter with on-premises deployment options"
        },
        {
          icon: "database",
          title: "Unified Knowledge Base",
          description: "Connect all your organizational knowledge for smarter AI responses and decisions"
        },
        {
          icon: "layers",
          title: "Customizable Models",
          description: "Fine-tune and customize AI models that understand your business context and terminology"
        },
        {
          icon: "code",
          title: "No-Code Configuration",
          description: "Configure complex AI workflows without writing code through our intuitive interface"
        },
        {
          icon: "lock",
          title: "Enterprise Security",
          description: "SOC2 compliant platform with role-based access control and audit logging"
        }
      ]
    },
    
    // Capabilities Section
    capabilities: {
      title: "Platform Capabilities",
      subtitle: "Comprehensive features designed for enterprise needs",
      items: [
        {
          icon: "server",
          title: "Infrastructure Integration",
          description: "Seamlessly deploy within your existing IT environment",
          benefits: [
            "Integration with enterprise authentication systems",
            "Hardware utilization optimization",
            "Scalable deployment options",
            "Support for on-premises and hybrid cloud"
          ]
        },
        {
          icon: "bot",
          title: "Agent Marketplace",
          description: "Ready-to-use specialized AI agents for your industry",
          benefits: [
            "Pre-built domain-specific agents",
            "Customizable templates and workflows",
            "Rapid deployment for common use cases",
            "Continuous updates and improvements"
          ]
        },
        {
          icon: "settings",
          title: "Advanced Controls",
          description: "Fine-grained control over your AI systems",
          benefits: [
            "Detailed performance monitoring",
            "Resource allocation management",
            "Explainable AI with transparency reports",
            "Versioning and rollback capabilities"
          ]
        },
        {
          icon: "network",
          title: "Enterprise Integration",
          description: "Connect with your existing business systems",
          benefits: [
            "API-first architecture",
            "Pre-built connectors for enterprise software",
            "Custom integration support",
            "Secure data exchange protocols"
          ]
        }
      ]
    },
    
    // Solutions Section
    solutions: {
      title: "AI Agents for Every Department",
      subtitle: "Purpose-built AI assistants that integrate with your workflows",
      items: [
        {
          title: "Legal Assistant",
          description: "AI agent that assists with contract review, case management, and legal research",
          benefits: [
            "Automated contract analysis",
            "Legal document management",
            "Case timeline monitoring",
            "Regulatory compliance checking"
          ]
        },
        {
          title: "HR Assistant",
          description: "Streamline recruitment, onboarding, and employee support processes",
          benefits: [
            "Resume screening automation",
            "Employee documentation management",
            "Policy question answering",
            "Onboarding process coordination"
          ]
        },
        {
          title: "Technical Support",
          description: "24/7 first-line support and troubleshooting automation",
          benefits: [
            "Automated issue categorization",
            "Step-by-step troubleshooting guides",
            "System monitoring alerts",
            "IT knowledge base management"
          ]
        },
        {
          title: "Customer Service",
          description: "Enhance customer interactions with intelligent assistance",
          benefits: [
            "Multi-channel support automation",
            "Request classification and routing",
            "Personalized customer responses",
            "Satisfaction monitoring and reporting"
          ]
        }
      ]
    },
    
    // Security Section
    security: {
      title: "Enterprise-Grade Security",
      subtitle: "Built with the highest security standards",
      items: [
        {
          icon: "lock",
          title: "Data Privacy",
          description: "Complete control of your data with no external sharing"
        },
        {
          icon: "shield",
          title: "Compliance Ready",
          description: "Designed to meet GDPR, HIPAA, and industry-specific regulations"
        },
        {
          icon: "server",
          title: "On-Premises Deployment",
          description: "Keep sensitive data behind your firewall with local deployments"
        },
        {
          icon: "users",
          title: "Access Control",
          description: "Fine-grained role-based permissions and access management"
        }
      ]
    },
    
    // CTA Section
    cta: {
      title: "Transform Your Enterprise with AI",
      subtitle: "Get started with a secure and scalable AI platform",
      button: "Start Now"
    },
    
    // Footer
    footer: {
      copyright: "All rights reserved.",
      links: {
        privacy: "Privacy Policy",
        terms: "Terms of Service",
        contact: "Contact"
      }
    }
  },
  
  // German Translation
  de: {
    // Navigation
    nav: {
      features: "Funktionen",
      solutions: "Lösungen",
      security: "Sicherheit",
      login: "Anmelden"
    },
    
    // Hero Section
    hero: {
      title: "KI-Agenten-Plattform für Unternehmen",
      subtitle: "Implementieren Sie leistungsstarke KI-Agenten mit vollständiger Kontrolle und Datensouveränität",
      cta: "Jetzt Starten",
      login: "Anmelden"
    },
    
    // Features Section
    features: {
      title: "Plattform-Funktionen",
      subtitle: "Eine KI-Plattform für Unternehmen, die auf Sicherheit und Leistung ausgelegt ist",
      items: [
        {
          icon: "brainCircuit",
          title: "Spezialisierte KI-Agenten",
          description: "Einsatzbereite KI-Agenten für spezifische Geschäftsfunktionen wie Recht, HR, Support und Kundenservice"
        },
        {
          icon: "shield",
          title: "Datensouveränität",
          description: "Behalten Sie alle Ihre Daten innerhalb Ihres Sicherheitsperimeters mit On-Premises-Bereitstellungsoptionen"
        },
        {
          icon: "database",
          title: "Einheitliche Wissensbasis",
          description: "Verbinden Sie das gesamte Unternehmenswissen für intelligentere KI-Antworten und Entscheidungen"
        },
        {
          icon: "layers",
          title: "Anpassbare Modelle",
          description: "Optimieren und passen Sie KI-Modelle an, die Ihren Geschäftskontext und Ihre Terminologie verstehen"
        },
        {
          icon: "code",
          title: "No-Code-Konfiguration",
          description: "Konfigurieren Sie komplexe KI-Workflows ohne Code durch unsere intuitive Benutzeroberfläche"
        },
        {
          icon: "lock",
          title: "Unternehmenssicherheit",
          description: "SOC2-konforme Plattform mit rollenbasierter Zugriffskontrolle und Audit-Protokollierung"
        }
      ]
    },
    
    // Capabilities Section
    capabilities: {
      title: "Plattform-Fähigkeiten",
      subtitle: "Umfassende Funktionen für Unternehmensanforderungen",
      items: [
        {
          icon: "server",
          title: "Infrastrukturintegration",
          description: "Nahtlose Implementierung in Ihre bestehende IT-Umgebung",
          benefits: [
            "Integration mit Unternehmensauthentifizierungssystemen",
            "Optimierung der Hardware-Nutzung",
            "Skalierbare Bereitstellungsoptionen",
            "Unterstützung für On-Premises und Hybrid-Cloud"
          ]
        },
        {
          icon: "bot",
          title: "Agenten-Marktplatz",
          description: "Einsatzbereite spezialisierte KI-Agenten für Ihre Branche",
          benefits: [
            "Vorgefertigte domänenspezifische Agenten",
            "Anpassbare Vorlagen und Workflows",
            "Schnelle Bereitstellung für gängige Anwendungsfälle",
            "Kontinuierliche Updates und Verbesserungen"
          ]
        },
        {
          icon: "settings",
          title: "Erweiterte Steuerung",
          description: "Präzise Kontrolle über Ihre KI-Systeme",
          benefits: [
            "Detaillierte Leistungsüberwachung",
            "Ressourcenzuweisungsmanagement",
            "Erklärbare KI mit Transparenzberichten",
            "Versionierung und Rollback-Funktionen"
          ]
        },
        {
          icon: "network",
          title: "Unternehmensintegration",
          description: "Verbindung mit Ihren bestehenden Geschäftssystemen",
          benefits: [
            "API-First-Architektur",
            "Vorgefertigte Konnektoren für Unternehmenssoftware",
            "Benutzerdefinierte Integrationsunterstützung",
            "Sichere Datenaustauschprotokolle"
          ]
        }
      ]
    },
    
    // Solutions Section
    solutions: {
      title: "KI-Agenten für jede Abteilung",
      subtitle: "Zweckgebundene KI-Assistenten, die sich in Ihre Workflows integrieren",
      items: [
        {
          title: "Rechtsassistent",
          description: "KI-Agent, der bei Vertragsüberprüfung, Fallmanagement und juristischer Recherche unterstützt",
          benefits: [
            "Automatisierte Vertragsanalyse",
            "Verwaltung von Rechtsdokumenten",
            "Überwachung des Fallzeitplans",
            "Überprüfung der Einhaltung von Vorschriften"
          ]
        },
        {
          title: "HR-Assistent",
          description: "Optimierung von Rekrutierungs-, Onboarding- und Mitarbeiterunterstützungsprozessen",
          benefits: [
            "Automatisierung der Lebenslaufprüfung",
            "Verwaltung von Mitarbeiterdokumenten",
            "Beantwortung von Richtlinienfragen",
            "Koordination des Onboarding-Prozesses"
          ]
        },
        {
          title: "Technischer Support",
          description: "24/7 First-Line-Support und Automatisierung der Fehlerbehebung",
          benefits: [
            "Automatisierte Problemkategorisierung",
            "Schrittweise Anleitungen zur Fehlerbehebung",
            "Systemüberwachungswarnungen",
            "IT-Wissensdatenbankverwaltung"
          ]
        },
        {
          title: "Kundenservice",
          description: "Verbesserung der Kundeninteraktionen durch intelligente Unterstützung",
          benefits: [
            "Automatisierung des Multi-Channel-Supports",
            "Klassifizierung und Weiterleitung von Anfragen",
            "Personalisierte Kundenantworten",
            "Zufriedenheitsüberwachung und Berichterstattung"
          ]
        }
      ]
    },
    
    // Security Section
    security: {
      title: "Sicherheit auf Unternehmensniveau",
      subtitle: "Mit höchsten Sicherheitsstandards entwickelt",
      items: [
        {
          icon: "lock",
          title: "Datenschutz",
          description: "Vollständige Kontrolle über Ihre Daten ohne externe Weitergabe"
        },
        {
          icon: "shield",
          title: "Compliance-bereit",
          description: "Entwickelt, um DSGVO, HIPAA und branchenspezifische Vorschriften zu erfüllen"
        },
        {
          icon: "server",
          title: "On-Premises-Bereitstellung",
          description: "Bewahren Sie sensible Daten hinter Ihrer Firewall mit lokalen Bereitstellungen"
        },
        {
          icon: "users",
          title: "Zugriffskontrolle",
          description: "Fein abgestufte rollenbasierte Berechtigungen und Zugriffsverwaltung"
        }
      ]
    },
    
    // CTA Section
    cta: {
      title: "Transformieren Sie Ihr Unternehmen mit KI",
      subtitle: "Starten Sie mit einer sicheren und skalierbaren KI-Plattform",
      button: "Jetzt starten"
    },
    
    // Footer
    footer: {
      copyright: "Alle Rechte vorbehalten.",
      links: {
        privacy: "Datenschutzrichtlinie",
        terms: "Nutzungsbedingungen",
        contact: "Kontakt"
      }
    }
  }
};
